const FOCUSABLE_SELECTOR = "input, textarea, [contenteditable='true']";

function queryFromRoot(root, selector) {
  if (!root || !selector) return null;
  if (root.matches && root.matches(selector)) return root;
  return root.querySelector ? root.querySelector(selector) : null;
}

/**
 * 移动端底部弹窗键盘避让：
 * 1. 将遮罩约束到 visualViewport，确保弹窗整体位于软键盘上方；
 * 2. 将当前聚焦输入框滚动到弹窗滚动区的安全范围内；
 * 3. 在旧 WebView 中用 window.resize 兜底。
 *
 * 组件通过自定义选项 keyboardAvoidance 提供：
 * maskSelector / sheetSelector / scrollSelector / bottomInset。
 */
export default {
  data() {
    return {
      activeInput: null,
    };
  },
  watch: {
    visible(value) {
      if (value) {
        this.$nextTick(this.startKeyboardAvoidance);
      } else {
        this.stopKeyboardAvoidance();
      }
    },
  },
  mounted() {
    if (this.visible) this.$nextTick(this.startKeyboardAvoidance);
  },
  beforeDestroy() {
    this.stopKeyboardAvoidance();
  },
  methods: {
    getKeyboardAvoidanceConfig() {
      return this.$options.keyboardAvoidance || {};
    },

    startKeyboardAvoidance() {
      if (this._keyboardAvoidanceActive) {
        this.scheduleKeyboardViewportUpdate();
        return;
      }
      const config = this.getKeyboardAvoidanceConfig();
      const mask = queryFromRoot(this.$el, config.maskSelector);
      const sheet = queryFromRoot(this.$el, config.sheetSelector);
      if (!mask || !sheet) return;

      this._keyboardAvoidanceActive = true;
      this._keyboardMaskEl = mask;
      this._keyboardSheetEl = sheet;
      this._keyboardBaseSheetHeight = Math.round(
        Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0) * 0.6
      );
      this._keyboardOriginalMaskStyle = {
        top: mask.style.top,
        bottom: mask.style.bottom,
        height: mask.style.height,
      };
      this._keyboardOriginalSheetMaxHeight = sheet.style.maxHeight;
      this._keyboardOriginalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const viewport = window.visualViewport;
      if (viewport) {
        viewport.addEventListener("resize", this.scheduleKeyboardViewportUpdate);
        viewport.addEventListener("scroll", this.scheduleKeyboardViewportUpdate);
      }
      window.addEventListener("resize", this.scheduleKeyboardViewportUpdate);
      this.scheduleKeyboardViewportUpdate();
    },

    stopKeyboardAvoidance() {
      const viewport = window.visualViewport;
      if (viewport) {
        viewport.removeEventListener("resize", this.scheduleKeyboardViewportUpdate);
        viewport.removeEventListener("scroll", this.scheduleKeyboardViewportUpdate);
      }
      window.removeEventListener("resize", this.scheduleKeyboardViewportUpdate);
      if (this.activeInput && this.activeInput.blur) this.activeInput.blur();
      this.activeInput = null;
      this.clearKeyboardTimers();

      if (this._keyboardRaf) {
        cancelAnimationFrame(this._keyboardRaf);
        this._keyboardRaf = null;
      }

      const mask = this._keyboardMaskEl;
      const sheet = this._keyboardSheetEl;
      const maskStyle = this._keyboardOriginalMaskStyle;
      if (mask && maskStyle) {
        mask.style.top = maskStyle.top;
        mask.style.bottom = maskStyle.bottom;
        mask.style.height = maskStyle.height;
      }
      if (sheet) sheet.style.maxHeight = this._keyboardOriginalSheetMaxHeight || "";
      if (this._keyboardAvoidanceActive) {
        document.body.style.overflow = this._keyboardOriginalBodyOverflow || "";
      }

      this.activeInput = null;
      this._keyboardAvoidanceActive = false;
      this._keyboardMaskEl = null;
      this._keyboardSheetEl = null;
    },

    clearKeyboardTimers() {
      (this._keyboardTimers || []).forEach(clearTimeout);
      this._keyboardTimers = [];
    },

    scheduleKeyboardViewportUpdate() {
      if (!this._keyboardAvoidanceActive || this._keyboardRaf) return;
      this._keyboardRaf = requestAnimationFrame(() => {
        this._keyboardRaf = null;
        this.updateKeyboardViewport();
      });
    },

    updateKeyboardViewport() {
      const mask = this._keyboardMaskEl;
      const sheet = this._keyboardSheetEl;
      if (!mask || !sheet) return;

      const viewport = window.visualViewport;
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      mask.style.top = `${Math.max(0, viewportTop)}px`;
      mask.style.bottom = "auto";
      mask.style.height = `${Math.max(0, viewportHeight)}px`;

      const maxHeight = Math.max(
        180,
        Math.min(this._keyboardBaseSheetHeight || viewportHeight, viewportHeight - 8)
      );
      sheet.style.maxHeight = `${maxHeight}px`;
      this.keepActiveInputVisible();
    },

    onFieldFocus(event) {
      const target = event.target;
      if (!target || !target.matches || !target.matches(FOCUSABLE_SELECTOR)) return;
      this.activeInput = target;
      this.clearKeyboardTimers();
      [0, 80, 240].forEach((delay) => {
        this._keyboardTimers.push(
          setTimeout(() => {
            this.scheduleKeyboardViewportUpdate();
            this.$nextTick(this.keepActiveInputVisible);
          }, delay)
        );
      });
    },

    onBodyFocusOut() {
      this._keyboardTimers = this._keyboardTimers || [];
      this._keyboardTimers.push(
        setTimeout(() => {
          const focused = document.activeElement;
          if (!this.$el || !focused || !this.$el.contains(focused)) {
            this.activeInput = null;
          }
        }, 0)
      );
    },

    keepActiveInputVisible() {
      const input = this.activeInput;
      const config = this.getKeyboardAvoidanceConfig();
      const scroll = queryFromRoot(this.$el, config.scrollSelector);
      const disconnected =
        input && typeof input.isConnected === "boolean" && !input.isConnected;
      if (!input || disconnected || !scroll) return;

      const viewport = window.visualViewport;
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const viewportBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      const inputRect = input.getBoundingClientRect();
      const scrollRect = scroll.getBoundingClientRect();
      const margin = Number(config.margin) || 16;
      const bottomInset = Number(config.bottomInset) || 0;
      const visibleTop = Math.max(scrollRect.top, viewportTop) + margin;
      const visibleBottom =
        Math.min(scrollRect.bottom, viewportBottom) - margin - bottomInset;
      if (visibleBottom <= visibleTop) return;

      let delta = 0;
      if (inputRect.bottom > visibleBottom) {
        delta = inputRect.bottom - visibleBottom;
      } else if (inputRect.top < visibleTop) {
        delta = inputRect.top - visibleTop;
      }
      if (delta) scroll.scrollTop += delta;
    },

    dismissKeyboard() {
      if (this.activeInput && this.activeInput.blur) this.activeInput.blur();
      this.activeInput = null;
    },
  },
};